<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import SuspensionGate from './features/auth/SuspensionGate.vue';
import VerificationBanner from './features/auth/VerificationBanner.vue';
import HeaderUsersStrip from './features/clients/HeaderUsersStrip.vue';
import QuickAddDialog from './features/tasks/QuickAddDialog.vue';
import ChamberWidget from './features/work-sessions/ChamberWidget.vue';
import { onWorkspaceUnverified } from './shared/api/client';
import {
  AccountMenu,
  AppBreadcrumbs,
  AppHeader,
  AppNav,
  AppToast,
  NotificationBell,
  ThemeToggle,
  type NavItem,
} from './shared/ui';
import SearchPopover from './shared/ui/SearchPopover.vue';
import { useAuthStore } from './stores/auth';
import { useBonusShopStore } from './stores/bonusShop';

const auth = useAuthStore();
const bonusShop = useBonusShopStore();
const route = useRoute();

const searchOpen = ref(false);
const quickAddOpen = ref(false);

const isCompanyUser = computed(() => auth.user?.membershipType === 'company');

const showEmployeeStrip = computed(
  () => auth.user?.membershipType === 'employee',
);

function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
}

function onGlobalKeydown(e: KeyboardEvent): void {
  if (!auth.isAuthenticated) return;
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    searchOpen.value = !searchOpen.value;
  } else if (e.key === '/' && !searchOpen.value && !isTypingTarget(e.target)) {
    e.preventDefault();
    searchOpen.value = true;
  } else if (
    e.key.toLowerCase() === 'n' &&
    !isCompanyUser.value &&
    !quickAddOpen.value &&
    !isTypingTarget(e.target)
  ) {
    e.preventDefault();
    quickAddOpen.value = true;
  } else if (e.key === 'Escape') {
    if (quickAddOpen.value) quickAddOpen.value = false;
    else if (searchOpen.value) searchOpen.value = false;
  }
}

onMounted(async () => {
  onWorkspaceUnverified(() => {
    auth.applyWorkspaceUnverified();
  });
  await auth.refresh();
  if (auth.isAuthenticated) {
    await bonusShop.loadProgram();
  }
  window.addEventListener('keydown', onGlobalKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onGlobalKeydown);
});

const showShell = computed(
  () => auth.isAuthenticated && route.meta['public'] !== true && !auth.user?.workspaceSuspended,
);

const showSuspensionGate = computed(
  () => auth.isAuthenticated && !!auth.user?.workspaceSuspended && route.meta['public'] !== true,
);

const navItems: NavItem[] = [
  { label: 'Главная', to: { name: 'home' }, exact: true },
  { label: 'Компании', to: { name: 'companies' } },
  { label: 'Календарь', to: { name: 'calendar' } },
  { label: 'Куча', to: { name: 'pile' } },
  { label: 'Отчеты', to: { name: 'reports' } },
];
</script>

<template>
  <SuspensionGate v-if="showSuspensionGate" />
  <template v-else-if="showShell">
    <div class="app-shell-wrap">
      <VerificationBanner />
      <AppHeader>
        <template #nav>
          <AppNav :items="navItems" />
        </template>
        <template
          v-if="showEmployeeStrip"
          #employees
        >
          <HeaderUsersStrip />
        </template>
        <template
          v-if="!isCompanyUser"
          #chamber
        >
          <ChamberWidget />
        </template>
        <template #breadcrumbs>
          <AppBreadcrumbs />
        </template>
        <template #tools>
          <NotificationBell />
          <button
            type="button"
            class="btn btn--ghost btn--small header-search-btn"
            aria-label="Поиск. Сочетание клавиш Ctrl+K или клавиша слэш"
            title="Поиск (Ctrl+K или /)"
            @click="searchOpen = true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
              />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </button>
          <ThemeToggle />
          <AccountMenu />
        </template>
      </AppHeader>
      <RouterView />
    </div>
    <SearchPopover
      :open="searchOpen"
      @close="searchOpen = false"
    />
    <QuickAddDialog
      :open="quickAddOpen"
      @close="quickAddOpen = false"
    />
    <AppToast />
  </template>
  <RouterView v-else />
</template>
